/**
 * TypeScript interfaces for DynamoDB table items
 * Account: 416411036610
 * Region: us-east-1
 */

// ============================================================================
// Control Mappings Table
// ============================================================================

export interface ControlMapping {
  // Primary Keys
  controlKey: string; // Partition key - source control identifier
  mappedControlKey: string; // Sort key - target control identifier

  // Attributes
  mappingKey: string; // Unique identifier for the mapping
  mappingWorkflowKey: string; // Workflow identifier
  status: MappingStatus; // Mapping status
  timestamp: string; // ISO 8601 timestamp

  // Additional attributes (not in schema but expected)
  sourceFramework?: string;
  targetFramework?: string;
  confidence?: number;
  mappingType?: 'direct' | 'partial' | 'related';
  createdBy?: string;
  lastModifiedBy?: string;
}

export type MappingStatus =
  | 'pending'
  | 'in-review'
  | 'approved'
  | 'rejected'
  | 'locked';

// ============================================================================
// Mapping Reviews Table
// ============================================================================

export interface MappingReview {
  // Primary Keys
  mappingKey: string; // Partition key - references ControlMapping.mappingKey
  reviewKey: string; // Sort key - unique review identifier (timestamp-based)

  // Control Keys (from mapping-reviews table)
  controlKey: string; // AWS Control key
  mappedControlKey: string; // Framework Control key

  // Attributes
  reviewerId?: string; // User who submitted the review
  submittedAt: string; // ISO 8601 timestamp
  status: string; // Status of the mapping review
  submittedBy?: {
    type: string;
    machineDetails?: {
      modelName: string;
      modelVersion: string;
    };
    timestamp: string;
  };

  // Additional attributes (expected)
  decision?: ReviewDecision;
  justification?: string;
  reviewerName?: string;
  reviewerEmail?: string;
  confidence?: number;
  tags?: string[];
  additionalInfo?: Record<string, any>;
}

export type ReviewDecision = 
  | 'approve' 
  | 'reject' 
  | 'final-approve' 
  | 'final-reject' 
  | 'suggest'
  | 'label-yes'
  | 'label-no';

// ============================================================================
// Mapping Feedback Table
// ============================================================================

export interface MappingFeedback {
  // Primary Keys
  mappingKey: string; // Partition key - references ControlMapping.mappingKey
  reviewerId: string; // Sort key - user who submitted feedback

  // Attributes
  timestamp: string; // ISO 8601 timestamp
  justification: string;
  isEdgeCaseMapping: boolean;
  reviewerName: string;
  status: FeedbackStatus;

  // Additional attributes (expected)
  reviewerEmail?: string;
  additionalNotes?: string;
  attachments?: string[];
}

export type FeedbackStatus = 'draft' | 'submitted' | 'locked';

// ============================================================================
// Frameworks Table
// ============================================================================

export interface Framework {
  // Primary Keys
  frameworkName: string; // Partition key - framework name
  version: string; // Sort key - framework version

  // Attributes
  frameworkKey: string; // Unique framework identifier
  status: FrameworkStatus;

  // Additional attributes (expected)
  description?: string;
  publishedDate?: string;
  effectiveDate?: string;
  expirationDate?: string;
  publisher?: string;
  category?: string;
  tags?: string[];
  metadata?: Record<string, any>;
  controlCount?: number;
  ownerBindleResource?: string;
}

export type FrameworkStatus = 'draft' | 'active' | 'deprecated' | 'archived';

// ============================================================================
// Framework Controls Table
// ============================================================================

export interface FrameworkControl {
  // Primary Keys
  frameworkKey: string; // Partition key - references Framework.frameworkKey
  controlKey: string; // Sort key - control identifier within framework

  // Attributes
  status: ControlStatus;

  // Additional attributes (expected)
  controlId: string; // Human-readable control ID (e.g., "AC-1", "SC-7")
  title: string;
  description: string;
  documentationUrl?: string; // URL to AWS documentation for the control
  category?: string;
  subcategory?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  implementationGuidance?: string;
  relatedControls?: string[];
  tags?: string[];
  metadata?: Record<string, any>;
  additionalInfo?: any; // DynamoDB List structure with additional reference information
}

export type ControlStatus = 'draft' | 'active' | 'deprecated' | 'archived';

// ============================================================================
// Common Controls Hierarchy Types (NEX-1062 - Compass Ontology)
// ============================================================================

/**
 * Represents a single level in the Compass Ontology hierarchy
 * Used for L1 (Domain), L2 (Objective), and L3 (Control) levels
 */
export interface HierarchyLevel {
  code: string;        // e.g., "AMGT1", "AMGT2.1", "AMGT3.1"
  title: string;       // e.g., "Asset management"
  description: string; // Full description of the hierarchy level
}

/**
 * Complete hierarchy structure for a Common Control
 * Contains all three levels of the Compass Ontology
 * Properties are optional since hierarchy extraction can fail or return partial data
 */
export interface ControlHierarchy {
  l1?: HierarchyLevel;  // Control Domain (top level)
  l2?: HierarchyLevel;  // Control Objective (mid level)
  l3?: HierarchyLevel;  // Common Control (specific control)
}

/**
 * Control Domain (L1) - Top level of Compass Ontology
 * Examples: Asset management, Access control, Data protection
 */
export interface ControlDomain {
  code: string;        // e.g., "AMGT1"
  title: string;       // e.g., "Asset management"
  description: string;
}

/**
 * Control Objective (L2) - Mid level of Compass Ontology
 * Examples: Asset inventory management, User access management
 */
export interface ControlObjective {
  code: string;        // e.g., "AMGT2.1"
  title: string;       // e.g., "Asset inventory management"
  description: string;
  domainCode: string;  // Reference to parent L1 code
}

/**
 * Common Control (L3) - Specific control in Compass Ontology
 * Examples: Asset inventory reconciliation and audit
 */
export interface CommonControl {
  controlKey: string;     // Full DynamoDB key
  code: string;           // e.g., "AMGT3.1"
  title: string;
  description: string;
  objectiveCode: string;  // Reference to parent L2 code
  domainCode: string;     // Reference to parent L1 code (for quick filtering)
  hierarchy?: ControlHierarchy;  // Optional since hierarchy extraction can fail
}

/**
 * Response structure for getCommonControlsWithHierarchy API call
 */
export interface CommonControlsHierarchyResponse {
  domains: ControlDomain[];      // Unique L1 values
  objectives: ControlObjective[]; // Unique L2 values with domain reference
  controls: CommonControl[];      // All controls with full hierarchy
}

// ============================================================================
// UI State Interfaces (for ReviewMappings component)
// ============================================================================

export interface UserReview {
  decision: ReviewDecision;
  userId: string;
  timestamp: string;
  reviewerName: string;
  justification: string;
  reviewHistory: ReviewHistoryEntry[];
}

export interface ReviewHistoryEntry {
  decision: ReviewDecision;
  timestamp: string;
  reviewerName: string;
  justification: string;
}

export interface FinalSubmission {
  justification: string;
  isEdgeCaseMapping: boolean;
  timestamp: string;
  userId: string;
  reviewerName: string;
  status: 'locked';
}

// ============================================================================
// Data Transformation Types
// ============================================================================

/**
 * Composite key format used in UI: "framework-controlId"
 * DynamoDB format: "framework#controlId"
 */
export interface CompositeKey {
  framework: string;
  controlId: string;
}

/**
 * Query parameters for fetching mapping data
 */
export interface MappingQueryParams {
  frameworkKey?: string;
  controlKey?: string;
  status?: MappingStatus;
  workflowKey?: string;
  limit?: number;
  nextToken?: string;
  includeReviews?: boolean;
}

/**
 * Query parameters for fetching reviews
 */
export interface ReviewQueryParams {
  mappingKey?: string;
  reviewerId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  nextToken?: string;
}

/**
 * Aggregated mapping data for UI display
 */
export interface MappingWithReviews {
  mapping: ControlMapping;
  reviews: MappingReview[];
  feedback?: MappingFeedback;
  sourceControl?: FrameworkControl;
  targetControl?: FrameworkControl;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface DynamoDBQueryResponse<T> {
  items: T[];
  count: number;
  scannedCount: number;
  lastEvaluatedKey?: Record<string, any>;
  nextToken?: string;
}

export interface DynamoDBBatchResponse<T> {
  items: T[];
  unprocessedKeys?: Record<string, any>[];
}

// ============================================================================
// Error Types
// ============================================================================

export interface DynamoDBError {
  code: string;
  message: string;
  statusCode?: number;
  requestId?: string;
  retryable?: boolean;
}

export class CrossAccountAccessError extends Error {
  constructor(
    message: string,
    public readonly accountId: string,
    public readonly region: string,
    public readonly originalError?: Error
  ) {
    super(message);
    this.name = 'CrossAccountAccessError';
  }
}

// ============================================================================
// Mapping Jobs (NexusEngine)
// ============================================================================

export type MappingJobStatus =
  | 'QUEUED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED';

export interface MappingJobConfig {
  sourceFramework?: string;
  targetFrameworks?: string[];
  // Legacy field — older jobs persisted only selectedFrameworks. Newer jobs
  // mirror targetFrameworks here for backward compatibility.
  selectedFrameworks?: string[];
  awsControlMode?: 'with_mappings' | 'without_mappings' | 'all';
  topK?: number;
  threshold?: number;
  rerankerType?: string;
  requestedBy: string;
}

export interface MappingJob {
  JobId: string;
  status: MappingJobStatus;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  config: MappingJobConfig;
  errorMessage?: string;
  sagemakerJobArn?: string;
  sagemakerJobName?: string;
  requestedBy: string;
}

export const MAPPING_JOB_TERMINAL_STATUSES: ReadonlyArray<MappingJobStatus> = [
  'COMPLETED',
  'FAILED',
];

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Helper type for partial updates
 */
export type PartialUpdate<T> = Partial<Omit<T, 'controlKey' | 'mappedControlKey' | 'mappingKey' | 'reviewKey' | 'frameworkKey' | 'frameworkName' | 'version'>>;

/**
 * Helper type for creating new items (without generated fields)
 */
export type CreateInput<T> = Omit<T, 'timestamp' | 'reviewKey'>;
